import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { classes } from "polytype";
import type {
  TldData,
  TldListItemData,
  TldListQueryData,
  TransferAuthenticationData,
} from "./types.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";

export class Tld extends ReferenceModel {
  public static ofTld(tld: string) {
    return new Tld(tld);
  }

  public static query = (query: TldListQueryData = {}) => {
    return new TldListQuery(query);
  };

  public getContactSchema = provideReact(async () => {
    return config.behaviors.tld.getContactSchemas(this.id);
  }, [this.id]);

  public static find = provideReact(async (tld: string) => {
    const data = await config.behaviors.tld.query({});
    const item = data.items.find((i) => i.tld === tld);
    if (item !== undefined) {
      return new TldDetailed(item);
    }
  });

  public static get = provideReact(async (tld: string) => {
    const item = await Tld.find(tld);
    assertObjectFound(item, "tld", tld);
    return item;
  });

  public static getContactSchemas = provideReact(async (tld: string) => {
    return await config.behaviors.tld.getContactSchemas(tld);
  });

  public findDetailed = provideReact(
    () => Tld.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<TldDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Tld.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<TldDetailed>>;
}

export class TldCommon extends classes(Tld, DataModel<TldData>) {
  public readonly tld: string;
  public readonly irtp: boolean;
  public readonly rgpDays: number;
  public readonly transferAuthentication: TransferAuthenticationData;
  public constructor(data: TldData) {
    super([data.tld], [data]);
    this.tld = data.tld;
    this.irtp = data.irtp;
    this.rgpDays = data.rgpDays;
    this.transferAuthentication = data.transferAuthentication;
  }
}

export class TldDetailed extends classes(TldCommon, DataModel<TldData>) {
  public constructor(data: TldData) {
    super([data], [data]);
  }
}

export class TldListItem extends classes(
  TldCommon,
  DataModel<TldListItemData>,
) {
  public constructor(data: TldListItemData) {
    super([data]);
  }
}

export class TldListQuery extends ListQueryModel<TldListQueryData> {
  public constructor(query: TldListQueryData = {}) {
    super(query);
  }

  public refine(query: TldListQueryData) {
    return new TldListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { ...query } = this.query;
    const { items, totalCount } = await config.behaviors.tld.query({
      ...query,
    });

    return new TldList(
      this.query,
      items.map((t) => new TldListItem(t)),
      totalCount,
    );
  }, [this.queryId, "tld"]);
}

export class TldList extends classes(TldListQuery, ListDataModel<TldListItem>) {
  public constructor(
    data: TldListQueryData,
    tlds: TldListItem[],
    totalCount: number,
  ) {
    super([data], [tlds, totalCount]);
  }
}
